import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, Plus, ArrowLeft, Send, Loader2 } from 'lucide-react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { GlassSelect } from '../ui/GlassSelect';
import { useAuth } from '../../contexts/AuthContext';
import {
    getSupportTickets,
    getSupportTicket,
    createSupportTicket,
    replySupportTicket,
    updateSupportTicket,
    SupportTicket,
    SupportTicketDetail,
} from '../../services/api';
import './TicketsPage.css';

const CATEGORY_OPTIONS = [
    { value: 'billing', label: 'Billing' },
    { value: 'bug', label: 'Bug / Error' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'general', label: 'General Enquiry' },
    { value: 'account', label: 'Account' },
];

const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
];

const STATUS_FILTERS = [
    { value: '', label: 'All' },
    { value: 'open', label: 'Open' },
    { value: 'closed', label: 'Closed' },
];

function formatDate(dateStr: string): string {
    try {
        return new Date(dateStr).toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return dateStr;
    }
}

function formatDateTime(dateStr: string): string {
    try {
        return new Date(dateStr).toLocaleString('en-AU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return dateStr;
    }
}

function getCategoryLabel(value: string): string {
    return CATEGORY_OPTIONS.find(o => o.value === value)?.label ?? value;
}

const TicketsPage: React.FC = () => {
    const { user } = useAuth();

    // List view state
    const [tickets, setTickets] = useState<SupportTicket[]>([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState('');

    // New ticket form state
    const [showForm, setShowForm] = useState(false);
    const [formSubject, setFormSubject] = useState('');
    const [formCategory, setFormCategory] = useState('general');
    const [formPriority, setFormPriority] = useState('medium');
    const [formMessage, setFormMessage] = useState('');
    const [formError, setFormError] = useState('');
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formSuccess, setFormSuccess] = useState(false);

    // Thread view state
    const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
    const [ticketDetail, setTicketDetail] = useState<SupportTicketDetail | null>(null);
    const [threadLoading, setThreadLoading] = useState(false);
    const [threadError, setThreadError] = useState('');
    const [replyBody, setReplyBody] = useState('');
    const [replySending, setReplySending] = useState(false);
    const [statusUpdating, setStatusUpdating] = useState(false);

    const fetchTickets = useCallback(async () => {
        setListLoading(true);
        setListError('');
        try {
            const params = statusFilter ? { status: statusFilter } : undefined;
            const data = await getSupportTickets(params);
            setTickets(data);
        } catch (err: any) {
            setListError(err?.response?.data?.detail ?? 'Failed to load tickets.');
        } finally {
            setListLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    const fetchTicketDetail = useCallback(async (ticketId: string) => {
        setThreadLoading(true);
        setThreadError('');
        try {
            const data = await getSupportTicket(ticketId);
            setTicketDetail(data);
        } catch (err: any) {
            setThreadError(err?.response?.data?.detail ?? 'Failed to load ticket.');
        } finally {
            setThreadLoading(false);
        }
    }, []);

    const handleOpenTicket = (ticketId: string) => {
        setSelectedTicketId(ticketId);
        setTicketDetail(null);
        setReplyBody('');
        fetchTicketDetail(ticketId);
    };

    const handleBack = () => {
        setSelectedTicketId(null);
        setTicketDetail(null);
        fetchTickets();
    };

    const handleSubmitForm = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError('');

        if (!formSubject.trim()) {
            setFormError('Subject is required.');
            return;
        }
        if (formMessage.trim().length < 20) {
            setFormError('Message must be at least 20 characters.');
            return;
        }

        setFormSubmitting(true);
        try {
            await createSupportTicket({
                subject: formSubject.trim(),
                category: formCategory,
                message: formMessage.trim(),
                priority: formPriority,
            });
            setFormSubject('');
            setFormCategory('general');
            setFormPriority('medium');
            setFormMessage('');
            setFormSuccess(true);
            setShowForm(false);
            fetchTickets();
            setTimeout(() => setFormSuccess(false), 6000);
        } catch (err: any) {
            setFormError(err?.response?.data?.detail ?? 'Failed to submit ticket.');
        } finally {
            setFormSubmitting(false);
        }
    };

    const handleSendReply = async () => {
        if (!ticketDetail || !replyBody.trim()) return;
        setReplySending(true);
        try {
            await replySupportTicket(ticketDetail.ticket_id, replyBody.trim());
            setReplyBody('');
            await fetchTicketDetail(ticketDetail.ticket_id);
        } catch (err: any) {
            setThreadError(err?.response?.data?.detail ?? 'Failed to send reply.');
        } finally {
            setReplySending(false);
        }
    };

    const handleStatusToggle = async () => {
        if (!ticketDetail) return;
        const newStatus = ticketDetail.status === 'open' ? 'closed' : 'open';
        setStatusUpdating(true);
        try {
            await updateSupportTicket(ticketDetail.ticket_id, { status: newStatus });
            await fetchTicketDetail(ticketDetail.ticket_id);
        } catch (err: any) {
            setThreadError(err?.response?.data?.detail ?? 'Failed to update ticket status.');
        } finally {
            setStatusUpdating(false);
        }
    };

    // ── Thread View ──────────────────────────────────────────────────────────────
    if (selectedTicketId !== null) {
        return (
            <div className="tickets-page">
                <div className="thread-view">
                    <button className="thread-back-btn" onClick={handleBack}>
                        <ArrowLeft size={14} />
                        Back to tickets
                    </button>

                    {threadLoading && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--text-muted)' }}>
                            <Loader2 size={16} className="spin" />
                            Loading ticket…
                        </div>
                    )}

                    {threadError && (
                        <div className="success-banner" style={{ borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', color: 'var(--danger)' }}>
                            {threadError}
                        </div>
                    )}

                    {ticketDetail && (
                        <>
                            <GlassCard style={{ padding: 'var(--space-4)' }}>
                                <div className="thread-header">
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', margin: '0 0 var(--space-2) 0' }}>
                                            {ticketDetail.subject}
                                        </h2>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', alignItems: 'center' }}>
                                            <span className={`ticket-badge ${ticketDetail.status}`}>
                                                {ticketDetail.status === 'open' ? 'Open' : 'Closed'}
                                            </span>
                                            <span className={`ticket-badge ${ticketDetail.priority}`}>
                                                {ticketDetail.priority.charAt(0).toUpperCase() + ticketDetail.priority.slice(1)}
                                            </span>
                                            <span className="ticket-badge closed">
                                                {getCategoryLabel(ticketDetail.category)}
                                            </span>
                                            <span className="ticket-time">
                                                Opened {formatDate(ticketDetail.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                    <GlassButton
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleStatusToggle}
                                        disabled={statusUpdating}
                                    >
                                        {statusUpdating
                                            ? 'Updating…'
                                            : ticketDetail.status === 'open'
                                                ? 'Close Ticket'
                                                : 'Reopen Ticket'}
                                    </GlassButton>
                                </div>
                            </GlassCard>

                            <GlassCard style={{ padding: 'var(--space-4)', flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                <div className="thread-messages">
                                    {ticketDetail.messages.length === 0 && (
                                        <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', textAlign: 'center', padding: 'var(--space-4)' }}>
                                            No messages yet.
                                        </div>
                                    )}
                                    {ticketDetail.messages.map(msg => {
                                        const isStaff = msg.is_staff_reply;
                                        const isCurrentUser = msg.author_id === user?.user_id;
                                        const bubbleType = isStaff ? 'staff' : 'user';
                                        return (
                                            <div key={msg.message_id} style={{ display: 'flex', flexDirection: 'column', alignItems: isStaff ? 'flex-end' : 'flex-start' }}>
                                                <div className={`message-bubble ${bubbleType}`}>
                                                    <div className="message-author">
                                                        {isStaff
                                                            ? 'AiMe Support'
                                                            : (msg.author_name || msg.author_email || (isCurrentUser ? (user?.full_name || user?.email) : 'You'))}
                                                        {' · '}
                                                        {formatDateTime(msg.created_at)}
                                                    </div>
                                                    <div className="message-body">{msg.body}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="reply-form">
                                    {ticketDetail.status === 'closed' ? (
                                        <div className="closed-note">
                                            This ticket is closed. Reopen it to send a reply.
                                        </div>
                                    ) : (
                                        <>
                                            <textarea
                                                className="ticket-textarea"
                                                placeholder="Write your reply…"
                                                value={replyBody}
                                                onChange={e => setReplyBody(e.target.value)}
                                                rows={4}
                                                disabled={replySending}
                                            />
                                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <GlassButton
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={handleSendReply}
                                                    disabled={replySending || !replyBody.trim()}
                                                >
                                                    {replySending ? (
                                                        <>
                                                            <Loader2 size={14} className="spin" />
                                                            Sending…
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Send size={14} />
                                                            Send Reply
                                                        </>
                                                    )}
                                                </GlassButton>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </GlassCard>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ── List View ────────────────────────────────────────────────────────────────
    const filteredTickets = statusFilter
        ? tickets.filter(t => t.status === statusFilter)
        : tickets;

    return (
        <div className="tickets-page">
            <div className="tickets-header">
                <h1 className="tickets-title">Support Tickets</h1>
                <GlassButton
                    variant="primary"
                    size="sm"
                    onClick={() => { setShowForm(v => !v); setFormError(''); setFormSuccess(false); }}
                >
                    <Plus size={16} />
                    New Ticket
                </GlassButton>
            </div>

            {formSuccess && (
                <div className="success-banner">
                    Ticket submitted! We'll get back to you within 24 hours.
                </div>
            )}

            {showForm && (
                <GlassCard className="new-ticket-form" style={{ padding: 'var(--space-5)' }}>
                    <form onSubmit={handleSubmitForm} noValidate>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
                            <span className="new-ticket-form-title">New Support Ticket</span>
                            <button
                                type="button"
                                onClick={() => { setShowForm(false); setFormError(''); }}
                                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--text-sm)' }}
                            >
                                Cancel
                            </button>
                        </div>

                        <GlassInput
                            label="Subject"
                            value={formSubject}
                            onChange={setFormSubject}
                            placeholder="Briefly describe your issue"
                        />

                        <div className="form-row" style={{ marginTop: 'var(--space-3)' }}>
                            <GlassSelect
                                label="Category"
                                options={CATEGORY_OPTIONS}
                                value={formCategory}
                                onChange={setFormCategory}
                            />
                            <GlassSelect
                                label="Priority"
                                options={PRIORITY_OPTIONS}
                                value={formPriority}
                                onChange={setFormPriority}
                            />
                        </div>

                        <div style={{ marginTop: 'var(--space-3)' }}>
                            <label style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--text-secondary)', display: 'block', marginBottom: 'var(--space-1)' }}>
                                Message
                            </label>
                            <textarea
                                className="ticket-textarea"
                                value={formMessage}
                                onChange={e => setFormMessage(e.target.value)}
                                placeholder="Describe your issue in detail (min. 20 characters)"
                                rows={5}
                            />
                        </div>

                        {formError && (
                            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--danger)', marginTop: 'var(--space-2)' }}>
                                {formError}
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-3)' }}>
                            <GlassButton
                                variant="primary"
                                size="sm"
                                type="submit"
                                disabled={formSubmitting}
                            >
                                {formSubmitting ? 'Submitting…' : 'Submit Ticket'}
                            </GlassButton>
                        </div>
                    </form>
                </GlassCard>
            )}

            <div className="tickets-filter-bar">
                {STATUS_FILTERS.map(f => (
                    <button
                        key={f.value}
                        className={`tickets-filter-btn${statusFilter === f.value ? ' active' : ''}`}
                        onClick={() => setStatusFilter(f.value)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {listError && (
                <div className="success-banner" style={{ borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', color: 'var(--danger)', marginBottom: 'var(--space-4)' }}>
                    {listError}
                </div>
            )}

            {listLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--text-muted)', padding: 'var(--space-6) 0' }}>
                    <Loader2 size={18} className="spin" />
                    Loading tickets…
                </div>
            ) : filteredTickets.length === 0 ? (
                <div className="tickets-empty">
                    <span style={{ fontSize: 'var(--text-base)', color: 'var(--text-secondary)' }}>
                        {statusFilter ? `No ${statusFilter} tickets.` : 'No tickets yet. Submit a ticket if you need help.'}
                    </span>
                    {!statusFilter && (
                        <GlassButton variant="primary" size="sm" onClick={() => setShowForm(true)}>
                            <Plus size={16} />
                            New Ticket
                        </GlassButton>
                    )}
                </div>
            ) : (
                <div className="tickets-list">
                    {filteredTickets.map(ticket => (
                        <div
                            key={ticket.ticket_id}
                            className="ticket-row"
                            onClick={() => handleOpenTicket(ticket.ticket_id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => e.key === 'Enter' && handleOpenTicket(ticket.ticket_id)}
                        >
                            <div className="ticket-info">
                                <div className="ticket-subject">{ticket.subject}</div>
                                <div className="ticket-meta">
                                    <span className={`ticket-badge ${ticket.status}`}>
                                        {ticket.status === 'open' ? 'Open' : 'Closed'}
                                    </span>
                                    <span className={`ticket-badge ${ticket.priority}`}>
                                        {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                                    </span>
                                    <span className="ticket-badge closed">
                                        {getCategoryLabel(ticket.category)}
                                    </span>
                                    {ticket.message_count !== undefined && (
                                        <span className="ticket-time">
                                            {ticket.message_count} {ticket.message_count === 1 ? 'message' : 'messages'}
                                        </span>
                                    )}
                                    <span className="ticket-time">{formatDate(ticket.created_at)}</span>
                                </div>
                            </div>
                            <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TicketsPage;
